//------------------------------------------------------------------------------
// BFA10104G 共架申込 通信設備
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

// 共通
import withRouter from '@/components/organisms/withRouter';
import AppLayout from '@/layouts/app-layout';
import PageTitle from '@/components/molecules/PageTitle';
import MessageArea from '@/components/molecules/WarningMessageArea';
import CustomStepper from '@/components/molecules/Stepper';
import commonOperations from '@/reducks/common/operations';
import backend from '@/common/backend';
import PoleItem from '@/utils/pole-item';
import { makeDataMap } from '@/utils/wijmo-utils';

// 共架申込
import MainPole from '@/pages/bfa/common/main-pole';
import TitledPanel from '@/components/titled-panel';
import OrderNumber from '@/pages/bfa/common/order-number';
import OrderInformations from '@/pages/bfa/common/order-info';
import OrderMapArea from './map';
import SetubiListGrid from './setubi-list';
import KeikanListGrid from './keikan-list';
import SetubiDialog from './setubi-dialog';
import KeikanDialog from './keikan-dialog';
import OperationPanel from './ope-panel';

// 定数
const PAGE_NAME = '共架申込 通信設備';
const LOGH = 'BFA10104G:';

/**
 * 共架申込 通信設備
 */
class OrderTusin extends React.Component {
  /**
   * コンストラクタ
   */
  constructor(props) {
    super(props);

    // ステート
    this.state = {
      warnings: [],       // 警告メッセージ
      orderInfo: {},      // 申込情報
      mainPole: null,     // 代表電柱
      devCodeMap: null,   // 架設設備コードマップ
      nearDevMap: null,   // 最接近配電設備コードマップ
      idoTypeMap: null,   // 異動種別マップ

      // タブ表示
      tabSelected: 'tab-setubi',

      // 設備一覧
      setubiList: [],          // 設備一覧
      setubiDialog: null,      // 編集ダイアログ表示設定
      setubiSelected: null,    // 選択(行クリック)した設備

      // 径間一覧
      keikanList: [],          // 径間一覧
      keikanDialog: null,      // 編集ダイアログ表示設定
      keikanSelected: null,    // 選択(行クリック)した径間
    };

    // 線下状況マップ
    const belowStatList = props.genericMap?.senka_jyokyo;
    this.belowStatMap = makeDataMap(belowStatList?.map((g) => ({
      value: g.cd,
      label: g.cdName,
    })));

    // 一覧グリッド参照
    this.setubiRef = React.createRef();
    this.keikanRef = React.createRef();
  }

  /**
   * マウント
   */
  async componentDidMount() {

    // 申込番号 (パスパラメーター)
    const orderNo = OrderNumber.from(this.props.params);

    // 申込取得
    const res = await backend.get('/bfa10104g/load/' + orderNo.path());
    if (res.parse(this.props.doShowMessage).ok) {
      this.parseResponse(res.body);
    }
  }

  // レスポンス解析
  parseResponse = (body) => {

    // 設備一覧にレコードIDを付加
    let recordId = 0;
    const setubiList = (body.setubiList || []).map((data) => ({
      ...data, recordId: recordId++,
      poleDisp: PoleItem.text(data),   // 表示用電柱番号も追加
    }));

    // 径間一覧にレコードIDを付加
    recordId = 0;
    const keikanList = (body.keikanList || []).map((data) => ({
      ...data, recordId: recordId++,

      // 表示用電柱番号も追加
      k1poleDisp: PoleItem.text({ poleNo: data.k1poleNo }),
      k2poleDisp: PoleItem.text({ poleNo: data.k2poleNo }),
    }));

    // 申込情報更新
    this.setState({
      orderInfo:  body.orderInfo ?? {},
      mainPole:   MainPole.of(body.orderInfo),
      setubiList: setubiList,
      keikanList: keikanList,
      devCodeMap: makeDataMap(body.devCodeList),
      nearDevMap: makeDataMap(body.nearDevList),
      idoTypeMap: makeDataMap(body.idoTypeList),
    });

    // 一覧更新
    this.setubiRef.current?.setItemsSource(setubiList);
    this.keikanRef.current?.setItemsSource(keikanList);
  }

  /**
   * 描画
   */
  render() {
    const { genericMap } = this.props;
    const orderNo = OrderNumber.from(this.props.params);
    const orderInfo = this.state.orderInfo;

    // タブ表示
    const tabSelected = this.state.tabSelected;

    // 保存可否
    const orderStatus = orderInfo.orderStatus;
    const savable = (orderStatus === '01');  // 作成中

    // 描画
    return (
      <AppLayout>

        {/* ページタイトル */}
        <PageTitle pageTitle={PAGE_NAME} />

        {/* メッセージ領域 */}
        <MessageArea messages={this.state.warnings}/>

        {/* 申込進捗 */}
        <Box sx={{ mt: 1 }}>
          <CustomStepper
            category="1"
            status={orderInfo.orderStatus}
            genericStatus={genericMap?.order_status ?? []}
          />
        </Box>

        {/* 申込情報 */}
        <OrderInformations
          orderNo={orderNo}
          orderInfo={orderInfo}
        />

        {/* 地図 */}
        <TitledPanel title="地図" sx={{ mt: 2 }}>
          <OrderMapArea
            mainPole={this.state.mainPole}
            keikanList={this.state.keikanList}
            setubiList={this.state.setubiList}
            setubiSelected={this.state.setubiSelected}
            keikanSelected={this.state.keikanSelected}

            // 設備イベント
            onSetubiAdd={this.handleSetubiAdd}
            onSetubiEdit={this.handleSetubiEdit}
            onSetubiDelete={this.handleSetubiDelete}

            // 径間イベント
            onKeikanAdd={this.handleKeikanAdd}
            onKeikanEdit={this.handleKeikanEdit}
            onKeikanDelete={this.handleKeikanDelete}
          />
        </TitledPanel>

        {/* 設備/径間タブ */}
        <TitledPanel sx={{ mt: 2 }}
          headers={
            <Tabs
              value={tabSelected}
              textColor="inherit"
              scrollButtons={false}
              onChange={this.handleTabChange}
              sx={{
                minHeight: 0,
                flexGrow: 1,
                '& .MuiTab-root': {
                  py: 1,
                  minWidth: 0,
                  minHeight: 0,
                  fontSize: '1rem',
                }
              }}
            >
              <Tab id="tab-setubi" value="tab-setubi" label="設備一覧" />
              <Tab id="tab-keikan" value="tab-keikan" label="径間一覧" />
            </Tabs>
          }
        >
          {/* 設備一覧タブ */}
          <div id="tabpanel-setubi" role="tabpanel">
            <SetubiListGrid
              gridRef={this.setubiRef}
              devCodeMap={this.state.devCodeMap}
              nearDevMap={this.state.nearDevMap}
              idoTypeMap={this.state.idoTypeMap}
              belowStatMap={this.belowStatMap}
              isReadOnly={!savable}
              isVisible={tabSelected === 'tab-setubi'}
              onItemEdit={this.handleSetubiEdit}
              onItemSelect={this.handleSetubiSelect}
              onItemUpdate={this.handleSetubiUpdate}
            />
          </div>

          {/* 径間一覧タブ */}
          <div id="tabpanel-keikan" role="tabpanel">
            <KeikanListGrid
              gridRef={this.keikanRef}
              devCodeMap={this.state.devCodeMap}
              nearDevMap={this.state.nearDevMap}
              idoTypeMap={this.state.idoTypeMap}
              belowStatMap={this.belowStatMap}
              isReadOnly={!savable}
              isVisible={tabSelected === 'tab-keikan'}
              onItemEdit={this.handleKeikanEdit}
              onItemSelect={this.handleKeikanSelect}
              onItemUpdate={this.handleKeikanUpdate}
            />
          </div>
        </TitledPanel>

        {/* 操作パネル */}
        <OperationPanel
          orderNo={orderNo}
          savable={savable}
          navigate={this.props.navigate}
          onSave={this.handleSave}
        />

        {/* 設備編集ダイアログ */}
        <SetubiDialog
          initValues={this.state.setubiDialog}
          devCodeMap={this.state.devCodeMap}
          nearDevMap={this.state.nearDevMap}
          belowStatMap={this.belowStatMap}
          onDialogClose={this.handleSetubiClose}
        />

        {/* 径間編集ダイアログ */}
        <KeikanDialog
          initValues={this.state.keikanDialog}
          idoTypeMap={this.state.idoTypeMap}
          devCodeMap={this.state.devCodeMap}
          belowStatMap={this.belowStatMap}
          onDialogClose={this.handleKeikanClose}
        />
      </AppLayout>
    );
  }

  /** タブ切替 */
  handleTabChange = (event, newValue)  => {
    console.info(LOGH, 'タブ切替', newValue);
    this.setState({ tabSelected: newValue });
  }

  /** 一時保存 */
  handleSave = async () => {
    console.info(LOGH, '一時保存');

    // 申込情報
    const orderNo = OrderNumber.from(this.props.params);
    const lastUpdate = this.state.orderInfo.updateTime;

    // 設備一覧
    const setubiList = this.state.setubiList?.map((setubi) => ({
      bukkenNo:     setubi.bukkenNo,       // 物件番号
      areaCode:     setubi.areaCode,       // 地区コード
      lineCode:     setubi.lineCode,       // 線路コード
      poleNo:       setubi.poleNo,         // 電柱番号
      setubiCode:   setubi.setubiCode,     // 架設設備コード
      idoType:      setubi.idoType,        // 異動種別
      tijyoHeight:  setubi.tijyoHeight,    // 地上高
      nearSetubi:   setubi.nearSetubi,     // 最接近配電設備
      rikakuHaiden: setubi.rikakuHaiden,   // 亜秘伝設備との離隔
      rikakuUpper:  setubi.rikakuUpper,    // 通信線との離隔/上位
      rikakuLower:  setubi.rikakuLower,    // 通信線との離隔/下位
      senkaJyokyo:  setubi.senkaJyokyo,    // 線下状況
    }));

    // 径間一覧
    const keikanList = this.state.keikanList?.map((keikan) => ({
      k1bukkenNo:  keikan.k1bukkenNo,      // 電柱(1) 物件番号
      k1areaCode:  keikan.k1areaCode,      // 電柱(1) 地区コード
      k1lineCode:  keikan.k1lineCode,      // 電柱(1) 線路コード
      k1poleNo:    keikan.k1poleNo,        // 電柱(1) 電柱番号
      k2bukkenNo:  keikan.k2bukkenNo,      // 電柱(2) 物件番号
      k2areaCode:  keikan.k2areaCode,      // 電柱(2) 地区コード
      k2lineCode:  keikan.k2lineCode,      // 電柱(2) 線路コード
      k2poleNo:    keikan.k2poleNo,        // 電柱(2) 電柱番号
      bukkenNo:    keikan.bukkenNo,        // 物件番号(径間)
      idoType:     keikan.idoType,         // 異動種別
      setubiCode:  keikan.setubiCode,      // 架設設備コード
      keikan:      keikan.keikan,          // 径間
      tido:        keikan.tido,            // 弛度
      rikakuTsn:   keikan.rikakuTsn,       // 径間離隔 通信線
      rikakuHkk:   keikan.rikakuHkk,       // 径間離隔 電力引込線
      senkaJyokyo: keikan.senkaJyokyo,     // 線下状況
    }));

    // 径間情報保存
    const req = { setubiList, keikanList, lastUpdate };
    const res = await backend.post('/bfa10104g/save/' + orderNo.path(), req);
    if (res.parse(this.props.doShowMessage).ok) {
      this.parseResponse(res.body);
    }
  }

  //----------------------------------------------------------------------------
  // 設備一覧 イベント処理
  //----------------------------------------------------------------------------
  /*
   * 設備追加
   */
  handleSetubiAdd = (pole) => {
    const view = this.setubiRef.current?.getView();
    if (view) {
      console.debug(LOGH, 'Add setubi', pole);

      // レコードID/物件番号の最大値
      const orgItems = view.sourceCollection;
      const max = orgItems.reduce((ret, item) => ({
        recordId: Math.max(ret.recordId, item.recordId),
        bukkenNo: Math.max(ret.bukkenNo, item.bukkenNo || 0),
      }), { recordId: 0, bukkenNo: 0 });

      // 新しい項目を作成
      const newItem = {
        ...pole,
        recordId: max.recordId + 1,
        bukkenNo: max.bukkenNo + 1,
        poleDisp: PoleItem.text(pole),
        idoType:  '1',
      };

      // 一覧に追加
      orgItems.push(newItem);
      view.refresh();
    }
  };

  /**
   * 設備編集 - ダイアログ表示
   */
  handleSetubiEdit = (setubi) => {
    console.debug(LOGH, 'Edit setubi', setubi);
    this.setState({ setubiDialog: setubi });
  };

  /**
   * 設備編集 - ダイアログ終了
   */
  handleSetubiClose = (result) => {
    this.setState({ setubiDialog: null });

    // キャンセル終了
    if (!result) return;

    // 設備一覧の該当項目を検索
    const view = this.setubiRef.current?.getView();
    const item = view && view.sourceCollection.find(
        (source) => source.recordId === result.recordId);

    // 設備一覧に編集結果を反映
    if (item) {
      view.editItem(item);
      item.setubiCode   = result.setubiCode;    // 架設設備コード
      item.nearSetubi   = result.nearSetubi;    // 最接近配電設備
      item.senkaJyokyo  = result.senkaJyokyo;   // 線下状況
      item.tijyoHeight  = result.tijyoHeight;   // 地上高
      item.rikakuHaiden = result.rikakuHaiden;  // 配電設備との離隔
      item.rikakuUpper  = result.rikakuUpper;   // 通信線との離隔 上位
      item.rikakuLower  = result.rikakuLower;   // 通信線との離隔 下位
      view.commitEdit();
    }
  };

  /**
   * 設備削除
   */
  handleSetubiDelete = (setubi) => {
    const view = this.setubiRef.current?.getView();
    if (view) {
      console.debug(LOGH, 'Delete setubi', setubi);

      // 設備一覧の該当項目を検索
      const item = view.sourceCollection.find(
          (source) => source.recordId === setubi.recordId);
      if (!item) {
        return;
      }

      // 設備一覧から削除
      view.remove(item);
    }
  };

  /**
   * 設備選択
   */
  handleSetubiSelect = (setubi) => {
    console.info(LOGH, '設備選択', setubi);

    // 新しい項目を作成して状態更新
    const setubiSelected = { ...setubi };
    this.setState({ setubiSelected });
  }

  /**
   * 設備一覧更新
   */
  handleSetubiUpdate = (newList) => {

    // 新しい一覧を作成して状態更新
    const setubiList = [ ...newList ];
    this.setState({ setubiList });
  };

  //----------------------------------------------------------------------------
  // 径間一覧 イベント処理
  //----------------------------------------------------------------------------
  /**
   * 径間追加
   */
  handleKeikanAdd = (link) => {
    const view = this.keikanRef.current?.getView();
    if (view && link) {
      console.debug(LOGH, 'Add keikan', link);

      // レコードIDと物件番号の最大値
      const orgItems = view.sourceCollection;
      const max = orgItems.reduce((ret, item) => ({
        recordId: Math.max(ret.recordId, item.recordId),
        bukkenNo: Math.max(ret.bukkenNo, item.bukkenNo || 0),
      }), { recordId: 0, bukkenNo: 0 });

      // 新しい径間データを作成
      const newItem = {
        recordId: max.recordId + 1,
        bukkenNo: max.bukkenNo + 1,

        // 電柱(1)
        k1areaCode:  link.pole1st?.areaCode,
        k1areaName:  link.pole1st?.areaName,
        k1lineCode:  link.pole1st?.lineCode,
        k1lineName:  link.pole1st?.lineName,
        k1poleNo:    link.pole1st?.poleNo,
        k1poleDisp:  link.pole1st?.text(),
        k1poleKind:  link.pole1st?.poleKind,
        k1latitude:  link.pole1st?.latitude,
        k1longitude: link.pole1st?.longitude,

        // 電柱(2)
        k2areaCode:  link.pole2nd?.areaCode,
        k2areaName:  link.pole2nd?.areaName,
        k2lineCode:  link.pole2nd?.lineCode,
        k2lineName:  link.pole2nd?.lineName,
        k2poleNo:    link.pole2nd?.poleNo,
        k2poleDisp:  link.pole2nd?.text(),
        k2poleKind:  link.pole2nd?.poleKind,
        k2latitude:  link.pole2nd?.latitude,
        k2longitude: link.pole2nd?.longitude,
      };

      // 一覧に追加
      orgItems.push(newItem);
      view.refresh();
    }
  }

  /**
   * 径間編集 - ダイアログ表示
   */
  handleKeikanEdit = (keikan) => {
    console.debug(LOGH, 'Edit keikan', keikan);
    this.setState({ keikanDialog: keikan });
  }

  /**
   * 径間編集 - ダイアログ終了
   */
  handleKeikanClose = (result) => {
    this.setState({ keikanDialog: null });

    // キャンセル終了
    if (!result) return;

    // 径間一覧の該当項目を検索
    const view = this.keikanRef.current?.getView();
    const item = view && view.sourceCollection.find(
        (source) => source.recordId === result.recordId);

    // 径間一覧に編集結果を反映
    if (item) {
      view.editItem(item);
      item.idoType      = result.idoType;       // 異動種別
      item.setubiCode   = result.setubiCode;    // 架設設備コード
      item.senkaJyokyo  = result.senkaJyokyo;   // 線下状況
      item.keikan       = result.keikan;        // 径間
      item.tido         = result.tido;          // 弛度
      item.rikakuTsn    = result.rikakuTsn;     // 径間離隔 通信線
      item.rikakuHkk    = result.rikakuHkk;     // 径間離隔 電力引込線
      view.commitEdit();
    }
  };

  /**
   * 径間削除
   */
  handleKeikanDelete = (keikan) => {
    const view = this.keikanRef.current?.getView();
    if (view) {
      console.debug(LOGH, 'Delete keikan', keikan);

      // 径間一覧の該当項目を検索
      const item = view.sourceCollection.find(
          (source) => source.recordId === keikan.recordId);
      if (!item) {
        return;
      }

      // 径間一覧から削除
      view.remove(item);
    }
  };

  /**
   * 径間選択
   */
  handleKeikanSelect = (keikan) => {
    console.info(LOGH, '径間選択', keikan);

    // 新しい項目を作成して状態更新
    const keikanSelected = { ...keikan };
    this.setState({ keikanSelected });
  }

  /**
   * 径間一覧更新
   */
  handleKeikanUpdate = (newList) => {
    console.debug(LOGH, 'Update keikan list', newList);

    // 新しい一覧を作成して状態更新
    const keikanList = [ ...newList ];
    this.setState({ keikanList });
  };
}

//------------------------------------------------------------------------------
// プロパティ
//------------------------------------------------------------------------------
OrderTusin.propTypes = {

  // ルーティング
  params: PropTypes.object,
  navigate: PropTypes.func,

  // ストア
  genericMap: PropTypes.object,
  doShowMessage: PropTypes.func.isRequired,
}

// ルーティング
OrderTusin = withRouter(OrderTusin);

// ストア接続
const mapStateToProps = (state) => ({
  genericMap: state.master.genericMap,
});
const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
};
OrderTusin = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderTusin);

// エクスポート
export default OrderTusin;
